import { useEffect, useMemo, useState } from 'react';
import { Mission, MissionAlternateRoute, MissionNamedRoute } from '../../services/missionApi';
import { missionRouteNames } from '../routePlanner/io/missionRoute';

export const MAX_NAMED_ROUTES = 4;
export const DEFAULT_ROUTE_LABELS = ['Primary', 'Secondary', 'Third', 'Fourth'] as const;

export interface NamedRouteSlot {
  id: string;
  label: string;
  live: boolean;
  draft: boolean;
  duration?: number;
  distance?: number;
  waypoints: Array<{
    label?: string | null;
    latitude: number;
    longitude: number;
    notes?: string | null;
  }>;
  geometry?: GeoJSON.LineString | null;
  alternate?: MissionAlternateRoute | null;
}

function fromStored(route: MissionNamedRoute, liveId: string): NamedRouteSlot {
  return {
    id: route.id,
    label: route.label || 'Route',
    live: route.id === liveId,
    draft: false,
    duration: route.stats?.duration,
    distance: route.stats?.distance,
    waypoints: route.waypoints || [],
    geometry: route.geometry,
    alternate: route.alternate,
  };
}

function liveSlot(mission: Mission, liveId: string): NamedRouteSlot {
  return {
    id: liveId,
    label: missionRouteNames(mission).primary,
    live: true,
    draft: false,
    duration: mission.route_stats?.duration,
    distance: mission.route_stats?.distance,
    waypoints: (mission.waypoints || []).map((w) => ({
      label: w.label,
      latitude: w.latitude,
      longitude: w.longitude,
      notes: w.notes,
    })),
    geometry: mission.route_geometry,
    alternate: mission.alt_route,
  };
}

export function composeNamedSlots(
  mission: Mission | null | undefined,
  drafts: NamedRouteSlot[] = [],
): NamedRouteSlot[] {
  const liveId = (mission?.active_route_id || 'primary').trim();
  const stored = (mission?.saved_routes || []).map((route) => fromStored(route, liveId));
  if (mission?.route_geometry && !stored.some((slot) => slot.id === liveId)) {
    stored.unshift(liveSlot(mission, liveId));
  }
  return [...stored, ...drafts.filter((draft) => !stored.some((slot) => slot.id === draft.id))];
}

export function createdNamedRoute(
  previousIds: Set<string>,
  saved: MissionNamedRoute[] | null | undefined,
): MissionNamedRoute | undefined {
  return (saved || []).find((item) => !previousIds.has(item.id));
}

export function isDraftRouteId(id: string | null | undefined): boolean {
  return !!id && id.startsWith('draft-');
}

/**
 * Stops that belong to the slot being edited.
 *
 * A draft or parked plan must never inherit the live mission waypoints —
 * that is how "Add Secondary" used to copy Primary.
 */
export function waypointsForEditor(
  editingId: string,
  editing: NamedRouteSlot | null,
  liveWaypoints: NamedRouteSlot['waypoints'] = [],
): NamedRouteSlot['waypoints'] {
  if (isDraftRouteId(editingId) || editing?.draft) return editing?.waypoints || [];
  if (editing && !editing.live) return editing.waypoints || [];
  return liveWaypoints;
}

export function nextNamedLabel(slots: NamedRouteSlot[]): string {
  const taken = new Set(slots.map((slot) => slot.label.trim().toLowerCase()));
  return DEFAULT_ROUTE_LABELS.find((label) => !taken.has(label.toLowerCase()))
    || `Route ${slots.length + 1}`;
}

/** Spare line for a named plan: "Test 3 Alternate". */
/** Parked and draft plans share one Save control — never "Save Primary". */
export function namedRouteSaveLabel(
  editing: NamedRouteSlot | null | undefined,
  fallback: string,
): string {
  if (editing && !editing.live) return 'Save route';
  return fallback;
}

export function fallbackNameFor(routeName: string): string {
  const base = routeName.trim() || 'Route';
  return / alternate$/i.test(base) ? base : `${base} Alternate`;
}

export function useNamedRouteEditor(mission: Mission | null | undefined) {
  const [editingId, setEditingId] = useState(mission?.active_route_id || 'primary');
  const [drafts, setDrafts] = useState<NamedRouteSlot[]>([]);

  useEffect(() => {
    setEditingId(mission?.active_route_id || 'primary');
    setDrafts([]);
  }, [mission?.id]);

  const slots = useMemo(() => composeNamedSlots(mission, drafts), [mission, drafts]);
  const editing = useMemo(() => {
    const found = slots.find((slot) => slot.id === editingId);
    if (found) return found;
    // A brand-new Secondary must not fall through to the live plan. That
    // fallback is what copied Primary's stops onto the next route.
    if (isDraftRouteId(editingId)) {
      const draft = drafts.find((slot) => slot.id === editingId);
      return draft || {
        id: editingId,
        label: nextNamedLabel(slots),
        live: false,
        draft: true,
        waypoints: [],
      };
    }
    return slots.find((slot) => slot.live) || null;
  }, [slots, editingId, drafts]);

  const addSlot = (): NamedRouteSlot | null => {
    if (slots.length >= MAX_NAMED_ROUTES) return null;
    const slot: NamedRouteSlot = {
      id: `draft-${Date.now().toString(36)}`,
      label: nextNamedLabel(slots),
      live: false,
      draft: true,
      waypoints: [],
    };
    setDrafts((current) => [...current, slot]);
    setEditingId(slot.id);
    return slot;
  };

  const dropDraft = (id: string) => {
    setDrafts((current) => current.filter((slot) => slot.id !== id));
    if (editingId === id) setEditingId(mission?.active_route_id || 'primary');
  };

  return {
    editingId,
    setEditingId,
    slots,
    editing,
    isLive: !!editing?.live && !editing.draft && !isDraftRouteId(editingId),
    canAdd: slots.length < MAX_NAMED_ROUTES,
    addSlot,
    dropDraft,
  };
}
